import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

const destinations = [
  {
    name: "United Kingdom",
    flag: "🇬🇧",
    image: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?auto=format&fit=crop&q=80&w=800",
    highlight: "World-class universities, post-study work visa, global recognition",
  },
  {
    name: "United States",
    flag: "🇺🇸",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&q=80&w=800",
    highlight: "Top-ranked institutions, STEM OPT, diverse programme options",
  },
  {
    name: "Canada",
    flag: "🇨🇦",
    image: "https://images.unsplash.com/photo-1530021232320-687d8e3dba54?auto=format&fit=crop&q=80&w=800",
    highlight: "Post-graduation work permits, pathway to PR, affordable tuition",
  },
  {
    name: "Germany",
    flag: "🇩🇪",
    image: "https://images.unsplash.com/photo-1599946347371-68eb71b16afc?auto=format&fit=crop&q=80&w=800",
    highlight: "Low to no tuition fees, strong engineering and STEM programmes",
  },
  {
    name: "France",
    flag: "🇫🇷",
    image: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&q=80&w=800",
    highlight: "Grandes Écoles, affordable living, post-study work rights in the EU",
  },
  {
    name: "Ireland",
    flag: "🇮🇪",
    image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&q=80&w=800",
    highlight: "English-speaking, tech industry hub, 2-year stay-back visa",
  },
  {
    name: "Dubai / UAE",
    flag: "🇦🇪",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&q=80&w=800",
    highlight: "Regional hub, tax-free salaries, proximity to India",
  },
  {
    name: "South Korea & Japan",
    flag: "🇰🇷🇯🇵",
    image: "https://images.unsplash.com/photo-1528360983277-13d401cdc186?auto=format&fit=crop&q=80&w=800",
    highlight: "Government scholarships, top STEM research, growing English programmes",
  },
  {
    name: "Singapore & Hong Kong",
    flag: "🇸🇬🇭🇰",
    image: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&q=80&w=800",
    highlight: "Asia's premier education hubs, global finance and tech careers",
  },
  {
    name: "Australia & New Zealand",
    flag: "🇦🇺🇳🇿",
    image: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?auto=format&fit=crop&q=80&w=800",
    highlight: "Post-study work rights of 2–4 years, strong research institutions",
  },
  {
    name: "India",
    flag: "🇮🇳",
    image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&q=80&w=800",
    highlight: "IIMs, IITs, Ashoka, and top private universities — globally competitive",
  },
  {
    name: "Europe",
    flag: "🇪🇺",
    image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&q=80&w=800",
    highlight: "Netherlands, Spain, Italy, Switzerland — diverse options, affordable tuition",
  },
];

export default function Destinations() {
  return (
    <section
      id="destinations"
      data-testid="destinations-section"
      style={{ padding: "100px 0", backgroundColor: "#F8FAFC" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ marginBottom: "52px" }}
        >
          <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
            Where We Guide
          </span>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(26px, 2.5vw, 36px)", fontWeight: "700", color: "#0F172A", margin: "12px 0 0", lineHeight: "1.25" }}>
            Popular destinations we advise for
          </h2>
        </motion.div>

        <div className="mw-grid-4" style={{ gap: "20px" }}>
          {destinations.map((dest, i) => (
            <motion.div
              key={dest.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 4) * 0.08 }}
              data-testid={`destination-${dest.name.toLowerCase().replace(/[\s/&]+/g, "-")}`}
              style={{
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: "#fff",
                border: "1px solid #E2E8F0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                transition: "all 0.3s",
              }}
              whileHover={{ y: -5, boxShadow: "0 16px 40px rgba(0,0,0,0.10)" }}
            >
              <div style={{ position: "relative", height: "140px", overflow: "hidden" }}>
                <img
                  src={dest.image}
                  alt={dest.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "60px", background: "linear-gradient(to top, rgba(0,0,0,0.4), transparent)" }} />
                <div style={{ position: "absolute", top: "10px", right: "10px", fontSize: "20px", lineHeight: "1" }}>
                  {dest.flag}
                </div>
              </div>
              <div style={{ padding: "14px 16px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "6px" }}>
                  <MapPin size={12} style={{ color: "#2B2F9E", flexShrink: 0 }} />
                  <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", fontWeight: "700", color: "#0F172A", margin: 0 }}>
                    {dest.name}
                  </h3>
                </div>
                <p style={{ fontSize: "12px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: "1.5" }}>
                  {dest.highlight}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          style={{ marginTop: "28px", fontSize: "14px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", textAlign: "center", fontStyle: "italic", padding: "14px 20px", backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px" }}
        >
          Final recommendations depend on fit, outcomes, budget, and readiness — not on popular trends.
        </motion.p>
      </div>
    </section>
  );
}
